/**
 * headerIconOverlap.js
 * Diagnostic plugin that verifies column header text does not collide or overlap
 * with column filter toggles or sorting arrows.
 */
export const headerIconOverlapPlugin = {
    id: 'header-icon-overlap',
    name: 'Column Header Icon & Text Overlap Detector',
    category: 'layout',
    description: 'Inspects column headers with active filter toggles and sort glyphs to ensure no bounding-box collision with title text.',

    async run(context) {
        const checks = [];
        const errors = [];
        let passed = true;

        if (context.targetEl) {
            // Live table inspection
            const tableEl = context.targetEl;
            const headers = tableEl.querySelectorAll('thead th');
            headers.forEach((th, idx) => {
                const check = inspectHeaderCell(th, `Live Table Column #${idx + 1}`, context);
                checks.push(check);
                if (!check.passed) {
                    passed = false;
                    errors.push(check.message);
                }
            });

            return {
                passed,
                checks,
                errors,
                details: passed
                    ? `All ${checks.length} header cells in target table maintain clean separation.`
                    : `${errors.length} header cell(s) have overlapping text or icons.`
            };
        }

        // Headless synthetic test scenario (recreating narrow columns with sort + filter)
        const result = await context.createTable(builder => {
            builder
                .withPaging(5)
                .withColumn(c => c.withData('Id').withTitle('ID').withFilter('text'))
                .withColumn(c => c.withData('Type').withTitle('TYPE').withFilter('select'))
                .withColumn(c => c.withData('Status').withTitle('STATUS').withFilter('select'))
                .withColumn(c => c.withData('Action').withTitle('ACTIONS'))
                .withRows([
                    { Id: 1, Type: 'Credit Memo', Status: 'Approved', Action: 'Edit' },
                    { Id: 2, Type: 'Debit Memo', Status: 'Pending', Action: 'Edit' }
                ]);
        });

        if (result.skipped) {
            return {
                passed: true,
                checks: [{ target: 'Synthetic Table', passed: true, message: `Skipped: ${result.skipReason}` }],
                errors: [],
                details: `Skipped DOM-level check — ${result.skipReason}`
            };
        }

        const { tableEl, destroy } = result;
        const headers = tableEl.querySelectorAll('thead th');
        headers.forEach(th => {
            const title = th.textContent.replace(/\s+/g, ' ').trim();
            const check = inspectHeaderCell(th, `Column "${title}"`, context);
            checks.push(check);
            if (!check.passed) {
                passed = false;
                errors.push(check.message);
            }
        });

        destroy();

        return {
            passed,
            checks,
            errors,
            details: passed
                ? `Passed: All ${checks.length} column headers have sufficient padding and zero icon collision.`
                : `Failed: ${errors.length} column header(s) exhibited text-icon overlap.`
        };
    }
};

function inspectHeaderCell(th, label, context) {
    const thRect = th.getBoundingClientRect();
    const filterToggle = th.querySelector('.dt-column-filter-toggle');
    const isSortable = th.classList.contains('sorting') || th.classList.contains('sorting_asc') || th.classList.contains('sorting_desc');
    const hasFilter = Boolean(filterToggle) || th.classList.contains('dt-has-filter');

    if (!hasFilter && !isSortable) {
        return {
            target: label,
            passed: true,
            message: `${label}: Plain header with no active icons.`
        };
    }

    const textRect = context.measureTextRect(th);
    const computed = window.getComputedStyle(th);
    const paddingRight = parseFloat(computed.paddingRight) || 0;

    // 1. If filter toggle is present, check direct bounding box collision with text
    if (filterToggle && textRect) {
        const toggleRect = filterToggle.getBoundingClientRect();
        const collides = context.measureCollision(textRect, toggleRect, 2);

        if (collides) {
            return {
                target: label,
                passed: false,
                message: `${label}: Text bounds [${Math.round(textRect.left)}..${Math.round(textRect.right)}] collide with filter toggle [${Math.round(toggleRect.left)}..${Math.round(toggleRect.right)}].`,
                data: { textRect, toggleRect, paddingRight }
            };
        }
    }

    // 2. If both sort and filter are enabled, verify padding-right is at least 50px
    if (isSortable && hasFilter && paddingRight < 50) {
        return {
            target: label,
            passed: false,
            message: `${label}: Has both sort and filter, but padding-right is only ${paddingRight}px (expected >= 50px to prevent overlap).`,
            data: { paddingRight, isSortable, hasFilter }
        };
    }

    // 3. If filter only, verify padding-right is at least 32px
    if (!isSortable && hasFilter && paddingRight < 32) {
        return {
            target: label,
            passed: false,
            message: `${label}: Has column filter toggle, but padding-right is only ${paddingRight}px (expected >= 32px).`,
            data: { paddingRight, hasFilter }
        };
    }

    return {
        target: label,
        passed: true,
        message: `${label}: Clean spacing maintained (padding-right: ${paddingRight}px, isSortable: ${isSortable}, hasFilter: ${hasFilter}).`,
        data: { paddingRight, isSortable, hasFilter }
    };
}
